<script lang="ts" setup>
import {
  ChatMessageImageType,
  InputTypeEnum,
  ModelList,
} from "@/enums/chatEnums";
import i18n from "@/locales";
import { useChat } from "@/stores/chat";
import { useChatSession } from "@/stores/chatSession";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { Response } from "@/types/api-types";
import { ChatMessage, Mate } from "@/types/entity";
import {
  Field,
  FieldInstance,
  Popover,
  showLoadingToast,
  showNotify,
} from "vant";
import { computed, ComputedRef, onMounted, onUnmounted, ref } from "vue";
import OptionReply from "./OptionReply.vue";

import { OptionReplyInstance } from "@/components/mobile/chat/v2/OptionReply.vue";
import { $bus } from "@/stores/eventBus";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const fastForwardTimeConfig: {
  text: ComputedRef<string>;
  interval: number;
  timeUnit: "minute" | "hour" | "day" | "week";
}[] = [
  {
    text: computed(() => t("Chats.fastForwardOneMinute")),
    interval: 1,
    timeUnit: "minute",
  },
  {
    text: computed(() => t("Chats.fastForwardOneHour")),
    interval: 1,
    timeUnit: "hour",
  },
  {
    text: computed(() => t("Chats.fastForwardOneDay")),
    interval: 1,
    timeUnit: "day",
  },
  {
    text: computed(() => t("Chats.fastForwardOneWeek")),
    interval: 1,
    timeUnit: "week",
  },
];

const props = withDefaults(
  defineProps<{
    mate?: Mate;
    mateSessionId: number;
    inputMsg: string;
    myMessage: string;
    isStreamReplying: boolean;
    streamMsg: string;
    isPostingMessage: "start" | "end" | false;
    showAutoPlayStatus?: boolean;
    placeholder?: string;
    fromName?: string;
    toName?: string[];
    inputType: InputTypeEnum;
    temperature?: number;
    enableShowBottomOptions?: boolean;
    enableFastForward?: boolean;
  }>(),
  {
    toName: () => [],
    showAutoPlayStatus: true,
    enableShowBottomOptions: true,
    enableFastForward: true,
  }
);
const emit = defineEmits<{
  (e: "focus", event: FocusEvent): void;
  (e: "blur"): void;
  (e: "update:inputMsg", val: string): void;
  (e: "update:myMessage", val: string): void;
  (e: "update:isStreamReplying", val: boolean): void;
  (e: "update:streamMsg", val: string): void;
  (
    e: "onStreamFinish",
    params: {
      userMsg?: ChatMessage;
      assistantMsg?: ChatMessage;
    }
  ): void;
  (e: "onPostMsgClick", toNames?: string[]): void;
  (e: "update:isPostingMessage", val: "start" | "end" | false): void;
}>();

const mateStore = useMate();
const userStore = useGlobalUser();
const chatSessionStore = useChatSession();
const chat = useChat();
const currentSession = computed(
  () => chatSessionStore.sessionMap[props.mateSessionId]
);
const messageCount = computed(() => {
  return chat.messageListBySessionId(props.mateSessionId).length;
});
const enableAgentChat = computed<boolean>(() => false);
const inputRef = ref<FieldInstance | null>(null);
const isPrivate = computed(() => {
  if (!currentSession.value) {
    return false;
  }
  return currentSession.value?.isPrivate === 1;
});
const currentInputType = computed(() =>
  props.inputMsg || !isPrivate.value || !props.enableFastForward
    ? props.inputType
    : InputTypeEnum.FORWARD
);
const showRequestSelfieBtn = computed(() => {
  return (
    currentSession.value?.isPrivate === 1 && props.mate?.chatImageMessageCount
  );
});
const bottomOptionRef = ref<HTMLDivElement | null>(null);
onClickOutside(bottomOptionRef, () => {
  showBottomOptions.value = false;
});
const optionReplyRef = ref<OptionReplyInstance | null>(null);
const isInputDisabled = computed(
  () => !!props.isPostingMessage || (!isPrivate.value && !props.inputMsg.trim())
);

// Lifecycle
onMounted(async () => {
  if (!isPrivate.value || userStore.isCNUser || messageCount.value > 1) return;
  if (userStore.user.subscriptionType > 2) {
    const res = await showOptionReply();
    if (res === "auth") {
      return;
    }
  }
});

// EventHandler
const postMessage = async ({
  isRegenerate = false,
  force = false,
  timeInterval,
  timeUnit,
  inputType,
  text,
  toNames,
  loadingPosition,
  targetId,
}: {
  isRegenerate?: boolean;
  force?: boolean;
  timeInterval?: number;
  timeUnit?: "minute" | "hour" | "day" | "week";
  inputType?: InputTypeEnum;
  text?: string;
  toNames?: string[];
  loadingPosition?: "start" | "end";
  targetId?: number;
} = {}) => {
  if (props.isPostingMessage && !force) {
    return;
  }
  loadingPosition =
    loadingPosition ??
    (props.inputMsg ||
    chat.getMsgByIdx(props.mateSessionId, 0)?.mateId ===
      mateStore.currentMate?.id
      ? "start"
      : "end");
  toNames = toNames ?? props.toName;
  emit("onPostMsgClick", toNames);
  emit("update:isPostingMessage", loadingPosition);
  const tmpVal = text ?? props.inputMsg;
  emit("update:myMessage", tmpVal);
  emit("update:inputMsg", "");
  // inputRef.value!.focus();
  try {
    // 1. 私密聊天 && 非NSFW
    const { cost } = ModelList.find(
      (v) =>
        v.value === currentSession.value?.model ||
        //@ts-ignore 处理 model 问题
        v.model === currentSession.value?.model
    ) || { cost: 1 };
    if (
      currentSession.value?.isPrivate === 1 &&
      currentSession.value?.isNsfw === 0 &&
      userStore.user.powerAmount < Number(cost)
    ) {
      chat.showInsufficientPowerPopup = true;
      chat.missPowerAmount = Number(cost) - userStore.user.powerAmount;
      return true;
    }

    let characterAction:
      | undefined
      | {
          type: InputTypeEnum;
          targetPositionId?: number;
          targetCharacterId?: number;
        };
    if (inputType === InputTypeEnum.FIGHT && targetId) {
      characterAction = {
        type: inputType,
        targetCharacterId: targetId,
      };
      inputType = InputTypeEnum.DO;
    } else if (
      (inputType === InputTypeEnum.MOVE || inputType === InputTypeEnum.SNEAK) &&
      targetId
    ) {
      characterAction = {
        type: inputType,
        targetPositionId: targetId,
      };
      inputType = InputTypeEnum.DO;
    }

    await chat.sendMessage(
      {
        mateSessionId: props.mateSessionId,
        text: tmpVal,
        type: inputType ?? currentInputType.value,
        isRegenerate,
        timeInterval,
        timeUnit,
        fromName: props.fromName,
        toNames,
        temperature: props.temperature,
        characterAction,
      },
      messageCallback
    );
  } catch (e) {
    emit("update:myMessage", tmpVal);
    throw e;
  } finally {
    emit("update:isPostingMessage", false);
  }
};

const requestImageMsg = async (type: ChatMessageImageType) => {
  if (!showRequestSelfieBtn.value) {
    showNotify({
      type: "warning",
      message: i18n.global.t("Chats.noPicAvailable"),
    });
    return;
  }
  if (props.isPostingMessage) {
    return;
  }
  try {
    emit("update:isPostingMessage", "start");
    const lastAssistantMsg = chat
      .messageListBySessionId(props.mateSessionId)
      .find((msg) => msg.mate.id !== mateStore.currentMate!.id);
    if (!lastAssistantMsg) return;
    const chatMsg = await chat.requestImageMessage(lastAssistantMsg.id!, type);
    if (!chatMsg || !chatMsg.answer) {
      showNotify({
        type: "warning",
        message: i18n.global.t("Chats.picExhausted"),
      });
      return;
    }
    chat.processChatResponse(currentSession.value.id!, chatMsg);
  } finally {
    emit("update:isPostingMessage", false);
  }
};
const messageCallback = async (content: Response<ChatMessage> | string) => {
  if (typeof content === "string") {
    emit("update:isStreamReplying", true);
    emit("update:streamMsg", props.streamMsg + content);
  } else {
    emit("update:isStreamReplying", false);
    emit("update:streamMsg", "");
    emit("update:myMessage", "");
    userStore.updateUserPartial({
      crystalQuantity: content.data.user.crystalQuantity,
      powerAmount: content.data.user.powerAmount,
      recentNsfwMessageCount: content.data.user.recentNsfwMessageCount,
    });
    const { userMsg, assistantMsg } = chat.processChatResponse(
      currentSession.value!.id!,
      content.data
    );
    emit("onStreamFinish", { userMsg, assistantMsg });
  }
};
const toggleAgentChat = async () => {
  const enable = !enableAgentChat.value;
  const loadingToast = showLoadingToast(
    enable
      ? i18n.global.t("Chats.proxyEnabling")
      : i18n.global.t("Chats.proxyDisabled")
  );
  try {
    await mateStore.toggleAgentChat(mateStore.currentMate!.uuid, enable);
  } finally {
    loadingToast.close();
  }
};

const showBottomOptions = ref(false);

const mode = ref("text");

const sendReply = (reply: string) => {
  emit("update:inputMsg", reply);
  mode.value = "text";
};
const showOptionReply = async () => {
  const res = optionReplyRef.value?.generateReplyOptional();
  mode.value = "options";
  return res;
};

onMounted(() => {
  $bus.$on("chat-message-regenerate", () =>
    postMessage({ isRegenerate: true })
  );
});

onUnmounted(() => {
  $bus.$off("chat-message-regenerate");
});

export interface ChatInputInstance {
  inputRef: FieldInstance | null;
  postMessage: typeof postMessage;
}

defineExpose({ inputRef, postMessage });
</script>
<template>
  <div
    class="w-full px-2 pb-2 mt-2 box-border relative flex flex-col transition-all duration-300 gap-2"
  >
    <div
      v-if="mode === 'text'"
      class="flex items-center justify-between h-full rounded-lg overflow-hidden"
    >
      <div class="flex items-end gap-0.5 flex-grow backdrop-blur-lg">
        <div
          :class="[
            'flex items-center justify-between flex-grow gap-1 rounded-lg',
            enableAgentChat ? 'bg-none' : 'bg-neutral text-neutral-content',
          ]"
        >
          <slot></slot>
          <div class="flex w-full gap-1 items-center justify-between px-2">
            <div class="flex items-center">
              <button
                v-if="!userStore.isCNUser && mode === 'text' && isPrivate"
                :disabled="!!isPostingMessage"
                class="btn btn-square sm:btn-sm btn-xs btn-ghost disabled:bg-transparent text-zinc-300"
                @click="
                  showOptionReply();
                  showBottomOptions = false;
                "
              >
                <i class="iconify mdi--idea" />
              </button>
            </div>
            <div class="flex items-end flex-grow rounded">
              <field
                ref="inputRef"
                :disabled="enableAgentChat"
                :enterkeyhint="$t('Chats.send')"
                :model-value="inputMsg"
                :placeholder="
                  enableAgentChat
                    ? i18n.global.t('Chats.proxyEnabled')
                    : placeholder ??
                      i18n.global.t('Chats.sendMsgTo', { name: mate?.name })
                "
                :style="{
                  '--van-field-input-text-color': 'white',
                }"
                autosize
                class="pl-2 pr-2 flex-1 after:hidden bg-transparent max-h-[80px] overflow-y-auto"
                rows="1"
                type="textarea"
                @blur="emit('blur')"
                @focus="emit('focus', $event)"
                @update:model-value="emit('update:inputMsg', $event)"
                @keydown.enter.prevent.stop="postMessage()"
              />
            </div>
            <div class="flex text-base-content/50 items-center">
              <button
                v-if="!isPrivate"
                :class="[
                  'btn btn-square btn-xs sm:btn-sm',
                  enableAgentChat ? 'btn-primary' : 'btn-ghost',
                ]"
                @click="toggleAgentChat"
              >
                <i class="iconify mdi--robot-outline" />
              </button>
              <popover v-if="showAutoPlayStatus" placement="top">
                <span class="text-xs px-2 py-4">
                  {{ $t("Chats.voiceAutoPlay") }}
                </span>
                <template #reference>
                  <i
                    v-if="
                      showAutoPlayStatus && currentSession?.autoPlayAudio === 1
                    "
                    :class="[
                      'iconfont icon-auto-play text-lg',
                      enableAgentChat ? 'text-primary' : 'text-zinc-300',
                    ]"
                  />
                </template>
              </popover>
              <button
                v-if="isPrivate && enableShowBottomOptions"
                :class="[
                  'btn btn-ghost btn-square btn-sm',
                  showBottomOptions ? 'text-primary' : 'text-zinc-300',
                ]"
                @click="showBottomOptions = !showBottomOptions"
              >
                <i
                  v-if="!showBottomOptions"
                  class="iconify mingcute--add-circle-line"
                />
                <i v-else class="iconify mingcute--close-circle-line" />
              </button>
              <VMenu
                v-if="currentInputType === InputTypeEnum.FORWARD"
                :delay="0"
                :popperHideTriggers="['click']"
                :triggers="['click']"
              >
                <div :disabled="isInputDisabled" class="mt-1 text-zinc-300">
                  <span
                    v-if="isPostingMessage"
                    class="loading loading-sm text-base-content/50"
                  />
                  <i v-else class="iconify ic--round-fast-forward" />
                </div>
                <template #popper>
                  <div
                    class="flex flex-col join join-vertical text-base-content rounded-none"
                  >
                    <span
                      class="join-item p-2 gap-1 text-base-content/50 border-b border-base-content/10 flex flex-col"
                    >
                      <span class="flex items-center gap-1 text-sm">
                        <i class="iconify mingcute--time-line text-xs" />
                        {{ $t("Chats.timeFastForward") }}
                      </span>
                      <span class="text-xs text-base-content/50">
                        {{ $t("Chats.timeFastForwardTip") }}
                      </span>
                    </span>
                    <button
                      v-for="(item, idx) in fastForwardTimeConfig"
                      :key="idx"
                      class="join-item btn btn-ghost btn-sm justify-start font-normal border-0 border-b border-base-content/10 last:border-b-0"
                      @click="
                        postMessage({
                          timeInterval: item.interval,
                          timeUnit: item.timeUnit,
                          inputType: InputTypeEnum.FORWARD,
                          loadingPosition: 'start',
                        })
                      "
                    >
                      {{ item.text.value }}
                    </button>
                  </div>
                </template>
              </VMenu>
              <div
                v-else
                :disabled="isInputDisabled"
                class="mt-1 text-zinc-300"
                @click.prevent="postMessage()"
              >
                <span
                  v-if="isPostingMessage"
                  class="loading loading-sm text-base-content/50"
                />
                <i v-else class="iconify mingcute--arrow-up-circle-fill" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-show="showBottomOptions"
      ref="bottomOptionRef"
      class="flex justify-evenly backdrop-blur rounded-lg border border-zinc-700/30 p-3"
    >
      <div class="indicator">
        <span
          v-if="!showRequestSelfieBtn"
          class="indicator-item badge badge-xs"
        >
          {{ $t("Chats.noPhoto") }}
        </span>
        <button
          :disabled="!showRequestSelfieBtn || !!isPostingMessage"
          class="flex flex-col gap-2 items-center aspect-square justify-center hover:bg-neutral/50 p-2 rounded-xl cursor-pointer w-20 disabled:text-zinc-600 disabled:pointer-events-none"
          @click="requestImageMsg(ChatMessageImageType.Pose)"
        >
          <i class="iconify mingcute--camera-2-line" />
          <span class="text-xs text-center font-semibold">
            {{ $t("Chats.sendMePhoto") }}
          </span>
        </button>
      </div>
    </div>
    <OptionReply
      v-show="mode === 'options'"
      ref="optionReplyRef"
      v-model:mode="mode"
      :mate-session-id="mateSessionId!"
      :type="inputType"
      :temperature="temperature"
      @reply-text="sendReply"
    />
  </div>
</template>
